import React, { useState, useEffect } from 'react';
import { Institution } from '../../types/institution';
import { FinicityConnectionResult } from '../../services/finicityService';
import { finicityCustomerService } from '../../services/finicityCustomerService';
import { useToast } from '../../contexts/ToastContext';
import './FinicityConnectSDK.css';

interface FinicityAccount {
  id: string;
  number: string;
  name: string;
  type: string;
  balance: number;
  accountNumberDisplay: string;
}

export interface FinicityConnectSDKProps {
  institution: Institution;
  onSuccess: (accountData: FinicityConnectionResult) => void;
  onCancel: () => void;
  onError: (error: string) => void;
  connectUrl?: string;
  userData?: any; // User data from /users/ API
}

type ConnectStep = 'loading' | 'ready' | 'connecting' | 'error';

const FinicityConnectSDK: React.FC<FinicityConnectSDKProps> = ({
  institution,
  onSuccess,
  onCancel,
  onError,
  connectUrl,
  userData // User data from /users/ API
}) => {
  const [currentStep, setCurrentStep] = useState<ConnectStep>('loading');
  const [error, setError] = useState<string>('');
  const { showToast } = useToast();

  // Initialize component and get connect URL (redirect flow)
  useEffect(() => {
    console.log('Component mounted, initializing connect...');
    initializeConnect();
  }, []);

  const initializeConnect = async () => {
    try {
      setCurrentStep('loading');

      // Build redirect URI - where Finicity will redirect back to
      const redirectUri = `${window.location.origin}/verification`;

      // Store data in localStorage before opening
      localStorage.setItem('finicity_institution_id', institution.id);
      localStorage.setItem('finicity_institution_name', institution.name);
      localStorage.setItem('finicity_user_id', userData?.id || userData?.user_id || '');

      console.log('Stored data before opening:', {
        institutionId: institution.id,
        institutionName: institution.name
      });

      // Get or create Finicity customer and connect URL with redirectUri
      const finicityCustomer = await finicityCustomerService.createConnectUrl({
        institutionId: institution.id,
        redirectUri: redirectUri
      });

      console.log('Connect URL received:', finicityCustomer.connectUrl);
      console.log('Redirect URI:', redirectUri);

      // Redirect to Finicity Connect URL in the same window
      if (finicityCustomer.connectUrl) {
        console.log('Redirecting to Finicity Connect:', finicityCustomer.connectUrl);
        window.location.href = finicityCustomer.connectUrl;
      } else {
        throw new Error('No connect URL received');
      }

    } catch (err) {
      console.error('Error initializing Finicity Connect:', err);
      const errorMessage = "We encountered an issue with your request. Please try again later or check your input.";
      setError(errorMessage);
      setCurrentStep('error');
      showToast({
        title: 'Connection Error',
        message: errorMessage
      });
      onError(errorMessage);
    }
  };


  // Render different states - show loader while waiting for user to complete flow in new window
  const renderContent = () => {
    switch (currentStep) {
      case 'loading':
        return (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>Processing the request...</span>
          </div>
        );

      case 'connecting':
        return (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>Redirecting to bank...</span>
          </div>
        );

      case 'error':
        return (
          <div className="finicity-state-container">
            <div className="finicity-header">
              <button onClick={onCancel} className="cancel-button">×</button>
            </div>
            <div className="finicity-content">
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ marginBottom: '20px', color: '#163224' }}>
                  {error || "We encountered an issue with your request. Please try again later or check your input."}
                </p>
                <button onClick={initializeConnect} className="retry-button">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <span>Loading...</span>
          </div>
        );
    }
  };

  return (
    <div className="finicity-connect-container">
      {renderContent()}
    </div>
  );
};

export default FinicityConnectSDK; 